import {
  accountsActions,
  getOrCreateUserNonce,
  initializePersistence,
  makeStore,
} from "@umami/state";

import { persistor, setupPersistor } from "./persistor";
import { IS_DEV } from "../env";

// Start with in-memory store without persistence
export const store = makeStore();

// Read plaintext rotation status from persisted accounts slice
export const getPersistedPasskeyRotationStatus = (): "Inactive" | "Success" | "Failure" => {
  try {
    const raw = localStorage.getItem("persist:accounts");

    if (!raw) {
      return "Inactive";
    }
    const parsed = JSON.parse(raw);
    const status = parsed?.passkeyRotationStatus;
    // remove quotes and convert it to string. '"Success"' -> "Success"

    const unquotedStatus = status?.replace(/^"(.*)"$/, "$1");

    return unquotedStatus === "Success" ||
      unquotedStatus === "Failure" ||
      unquotedStatus === "Inactive"
      ? unquotedStatus
      : "Inactive";
  } catch {
    return "Inactive";
  }
};

// Function to enable persistence after authentication
export const setupPersistence = async (key: string, callback?: () => Promise<void>) => {
  if (persistor) {
    await callback?.();

    return;
  }
  try {
    const nonce = await getOrCreateUserNonce(key);

    if (!nonce) {
      throw new Error("Failed to create nonce");
    }

    const prevRotation = store.getState().accounts.passkeyRotationStatus as
      | "Inactive"
      | "Success"
      | "Failure"
      | undefined;

    const newEncryptedDek = store.getState().accounts.newEncryptedDek;

    const { persistor } = initializePersistence(store, nonce);
    setupPersistor(persistor);

    const applyRotationSideEffects = () => {
      store.dispatch(accountsActions.setPasskeyRotationStatus({ status: prevRotation! }));

      if (prevRotation === "Success" && newEncryptedDek) {
        store.dispatch(
          accountsActions.setEncryptedDek({
            pkh: newEncryptedDek.pkh,
            encrypted_dek: newEncryptedDek.encrypted_dek,
          })
        );
        store.dispatch(accountsActions.clearNewEncryptedDek());
      }
    };

    if (prevRotation) {
      if (persistor.getState().bootstrapped) {
        applyRotationSideEffects();
      } else {
        const unsubscribe = persistor.subscribe(() => {
          if (!persistor.getState().bootstrapped) {
            return;
          }
          applyRotationSideEffects();
          unsubscribe();
        });
      }
    }

    // Wait for persistor to be bootstrapped before running callback
    if (persistor.getState().bootstrapped) {
      await callback?.();
    } else {
      const unsubscribe = persistor.subscribe(async () => {
        if (!persistor.getState().bootstrapped) {
          return;
        }
        await callback?.();
        unsubscribe();
      });
    }

    store.dispatch(accountsActions.setDefaultAccount());
  } catch (error) {
    console.error("Failed to initialize persistence:", error);

    return;
  }
};

if (IS_DEV) {
  // @ts-ignore use for debugging
  window.store = store;
}
